export type WorkPlacement =
  | "home.hero"
  | "services.hero"
  | "products.hero"
  | "products.gallery"
  | "projects.hero"
  | "about.hero"
  | "contact.hero"
  | "projects.feature";

export type WorkImage = {
  id: string;
  name: string;
  src: `/work_${string}.${"png" | "jpeg" | "jpg" | "webp"}`;
  width: number;
  height: number;
  title: string;
  alt: string;
  description: string;
  tags: readonly string[];
  placements: readonly WorkPlacement[];
};

export type WorkProject = {
  id: string;
  name: string;
  title: string;
  description: string;
  tags: readonly string[];
  images: readonly WorkImage[];
};

export const workImages = {
  poolEnclosure: {
    id: "work-001",
    name: "Pool Enclosure Gate",
    src: "/work_001.png",
    width: 1448,
    height: 1086,
    title: "Pool enclosure gate and fence",
    alt: "Black metal gate and fence enclosing a residential swimming pool",
    description:
      "Black perimeter metalwork with an arched pedestrian gate around a residential pool.",
    tags: ["gate", "fence", "pool enclosure", "exterior metalwork"],
    placements: ["products.gallery"],
  },
  slidingDrivewayGate: {
    id: "work-002",
    name: "Sliding Driveway Gate",
    src: "/work_002.png",
    width: 1448,
    height: 1086,
    title: "Sliding driveway gate",
    alt: "Black sliding metal driveway gate installed in front of a residence",
    description:
      "A wide sliding gate fabricated for a residential driveway entrance.",
    tags: ["sliding gate", "driveway gate", "residential metalwork"],
    placements: ["products.gallery"],
  },
  balconyRailings: {
    id: "work-003",
    name: "Exterior Balcony Railings",
    src: "/work_003.png",
    width: 1448,
    height: 1086,
    title: "Exterior balcony railings",
    alt: "Black metal balcony railings installed across a multi-unit residential building",
    description:
      "Coordinated exterior railings installed across multiple residential balconies.",
    tags: ["balcony railing", "exterior railing", "residential metalwork"],
    placements: ["home.hero"],
  },
  curvedInteriorRailing: {
    id: "work-004",
    name: "Rognis's Residence",
    src: "/work_004.png",
    width: 1448,
    height: 1086,
    title: "Rognis's Residence",
    alt: "Curved silver-tone metal railing overlooking an interior stairwell",
    description:
      "A continuous curved railing shaped to follow an elevated interior opening.",
    tags: ["curved railing", "interior railing", "custom metalwork"],
    placements: ["projects.feature"],
  },
  architecturalLouvers: {
    id: "work-005",
    name: "Architectural Louvers",
    src: "/work_005.png",
    width: 1536,
    height: 1024,
    title: "Exterior architectural louvers",
    alt: "Dark metal louver panels installed on a masonry building exterior",
    description:
      "Exterior louver assemblies integrated with a masonry building facade.",
    tags: ["architectural louvers", "exterior metalwork", "screening"],
    placements: ["products.gallery"],
  },
  drivewaySwingGate: {
    id: "work-006",
    name: "Driveway Swing Gate",
    src: "/work_006.png",
    width: 1619,
    height: 972,
    title: "Driveway swing gate",
    alt: "Black double swing gate across a narrow residential driveway",
    description:
      "A double-leaf metal gate with vertical pickets installed across a driveway entrance.",
    tags: ["swing gate", "driveway gate", "residential metalwork"],
    placements: ["contact.hero"],
  },
  poolAccessGate: {
    id: "work-007",
    name: "Pool Access Gate",
    src: "/work_007.png",
    width: 1448,
    height: 1086,
    title: "Pool access gate and fence",
    alt: "Black metal pedestrian gate and fence surrounding a residential pool",
    description:
      "A coordinated metal fence and self-contained pedestrian gate around a pool area.",
    tags: ["pool gate", "pool fence", "pedestrian gate"],
    placements: ["about.hero"],
  },
  pairedEntryGates: {
    id: "work-008",
    name: "Paired Entry Gates",
    src: "/work_008.png",
    width: 1448,
    height: 1086,
    title: "Paired entry gates",
    alt: "Two black metal entry gates with vertical pickets and keypad access",
    description:
      "Matching metal gates define separate access points along an exterior walkway.",
    tags: ["entry gate", "access control", "exterior metalwork"],
    placements: ["projects.hero"],
  },
  residentialLouverScreen: {
    id: "work-009",
    name: "Residential Louver Screen",
    src: "/work_009.png",
    width: 1254,
    height: 1254,
    title: "Architectural louver screen",
    alt: "Large bronze-tone horizontal louver screen on a residential building exterior",
    description:
      "A multi-story exterior louver system provides screening alongside residential balconies.",
    tags: ["architectural louvers", "facade screening", "exterior metalwork"],
    placements: ["services.hero"],
  },
  patternedCanopy: {
    id: "work-010",
    name: "Patterned Outdoor Canopy",
    src: "/work_010.jpeg",
    width: 1600,
    height: 1125,
    title: "Patterned outdoor canopy",
    alt: "Black metal outdoor canopy with decorative perforated roof panels",
    description:
      "A freestanding canopy combines structural framing with patterned overhead panels.",
    tags: ["canopy", "perforated panels", "architectural metalwork"],
    placements: ["home.hero"],
  },
  commercialEntryCanopy: {
    id: "work-011",
    name: "Commercial Entry Canopy",
    src: "/work_011.jpeg",
    width: 1600,
    height: 1062,
    title: "Commercial entry canopy",
    alt: "Black suspended metal canopy above a commercial storefront entrance",
    description:
      "A suspended linear canopy extends across a glazed commercial storefront.",
    tags: ["commercial canopy", "entry canopy", "architectural metalwork"],
    placements: ["products.hero"],
  },
  perforatedFacadeScreen: {
    id: "work-012",
    name: "Perforated Facade Screen",
    src: "/work_012.jpeg",
    width: 1200,
    height: 800,
    title: "Perforated facade screen",
    alt: "White perforated metal screen with circular openings across a building facade",
    description:
      "Large perforated panels form a continuous patterned screen across an upper facade.",
    tags: ["decorative screen", "perforated panels", "facade"],
    placements: ["home.hero"],
  },
  wellsSpringsApts: {
    id: "work-027",
    name: "Wellsprings Apts",
    src: "/work_027.jpeg",
    width: 1200,
    height: 800,
    title: "Wellsprings Apts",
    alt: "Black metal balcony railings installed across a multi-unit residential building",
    description:
      "Coordinated exterior railings installed across multiple residential balconies.",
    tags: ["balcony railing", "exterior railing", "residential metalwork"],
    placements: ["home.hero"],
  },
  nomiProject: {
    id: "work-028",
    name: "Nomi Project",
    src: "/work_028.jpeg",
    width: 1200,
    height: 800,
    title: "Nomi ",
    alt: "Metal balcony railings installed across a multi-unit residential building",
    description:
      "Coordinated exterior railings installed across multiple residential balconies.",
    tags: ["railing", "exterior railing", "residential metalwork"],
    placements: ["home.hero"],
  },
  autoZoneProject: {
    id: "work-029",
    name: "AutoZone Project",
    src: "/work_029.jpeg",
    width: 1200,
    height: 800,
    title: "AutoZone Project",
    alt: "Red-framed commercial service gate with timber infill panels and red safety bollards beside a white industrial building",
    description:
      "Commercial service gate installation with timber infill panels, structural steel framing, and protective bollards on a dedicated concrete pad.",
    tags: [
      "commercial gate",
      "service gate",
      "timber infill",
      "steel frame",
      "bollards",
      "exterior metalwork",
    ],
    placements: ["projects.feature"],
  },
  greisyResidence: {
    id: "work-032",
    name: "Greisy Residence",
    src: "/work_032.jpeg",
    width: 1200,
    height: 800,
    title: "Greisy Residence",
    alt: "Black chain-link perimeter fence with steel posts installed along a tree-lined residential property",
    description:
      "Residential perimeter fencing project with black chain-link mesh and steel framing defining the site boundary across a large outdoor property.",
    tags: [
      "residential fencing",
      "perimeter fence",
      "chain-link fence",
      "steel posts",
      "exterior metalwork",
      "boundary enclosure",
    ],
    placements: ["projects.feature"],
  },
  northernTrustBonitaSprings: {
    id: "work-033",
    name: "Northern Trust Bonita Springs",
    src: "/work_33.jpeg",
    width: 1200,
    height: 800,
    title: "Northern Trust Bonita Springs",
    alt: "New MC12x31 Channel @ ES of Existing Concrete Beam w/ Threaded Rods at 12\" O.C. Max, Staggered & New Steel Columns HSS4x4x1/4 x 12'8\"H BP3/4\"x6\"x10\" TP3/4\"x6\"x12\"",
    description: "Steel Support Structure @ New Open",
    tags: ["balcony railing", "exterior railing", "residential metalwork"],
    placements: ["projects.feature"],
  },
  cobsRiversideParkFenceFront: {
    id: "work-035",
    name: "COBS Riverside Park",
    src: "/work_35.jpeg",
    width: 1600,
    height: 739,
    title: "COBS Riverside Park",
    alt: "Black aluminum decorative fence installed around a monument base at a park construction site",
    description: "Aluminum Decorative Fence",
    tags: [
      "aluminum fence",
      "decorative fence",
      "park metalwork",
      "monument fence",
      "exterior metalwork",
    ],
    placements: ["projects.feature"],
  },
  cobsRiversideParkFenceMemorial: {
    id: "work-036",
    name: "COBS Riverside Park",
    src: "/work_36.jpeg",
    width: 1600,
    height: 739,
    title: "COBS Riverside Park",
    alt: "Black aluminum decorative fence enclosing the base of a memorial monument at Riverside Park",
    description: "Aluminum Decorative Fence",
    tags: [
      "aluminum fence",
      "decorative fence",
      "park metalwork",
      "memorial fence",
      "exterior metalwork",
    ],
    placements: ["projects.feature"],
  },
  cobsRiversideParkFenceRear: {
    id: "work-037",
    name: "COBS Riverside Park",
    src: "/work_37.jpeg",
    width: 1600,
    height: 739,
    title: "COBS Riverside Park",
    alt: "Rear view of a black aluminum decorative fence around a monument base at Riverside Park",
    description: "Aluminum Decorative Fence",
    tags: [
      "aluminum fence",
      "decorative fence",
      "park metalwork",
      "monument fence",
      "exterior metalwork",
    ],
    placements: ["projects.feature"],
  }

} satisfies Record<string, WorkImage>;

export const workImageSets = {
  homeHero: [
    workImages.balconyRailings,
    workImages.patternedCanopy,
    workImages.perforatedFacadeScreen,
  ],
  servicesHero: [workImages.residentialLouverScreen],
  productsHero: [workImages.commercialEntryCanopy],
  productsGallery: [
    workImages.poolEnclosure,
    workImages.slidingDrivewayGate,
    workImages.architecturalLouvers,
  ],
  projectsHero: [workImages.pairedEntryGates],
  aboutHero: [workImages.poolAccessGate],
  contactHero: [workImages.drivewaySwingGate],
  projectsFeature: [
    workImages.curvedInteriorRailing,
    workImages.wellsSpringsApts,
    workImages.nomiProject,
    workImages.autoZoneProject,
    workImages.greisyResidence,
    workImages.northernTrustBonitaSprings,
    workImages.cobsRiversideParkFenceFront,
  ]
} as const;

export const workProjects: readonly WorkProject[] = [
  {
    id: workImages.curvedInteriorRailing.id,
    name: workImages.curvedInteriorRailing.name,
    title: workImages.curvedInteriorRailing.title,
    description: workImages.curvedInteriorRailing.description,
    tags: workImages.curvedInteriorRailing.tags,
    images: [workImages.curvedInteriorRailing],
  },
  {
    id: workImages.wellsSpringsApts.id,
    name: workImages.wellsSpringsApts.name,
    title: workImages.wellsSpringsApts.title,
    description: workImages.wellsSpringsApts.description,
    tags: workImages.wellsSpringsApts.tags,
    images: [workImages.wellsSpringsApts],
  },
  {
    id: workImages.nomiProject.id,
    name: workImages.nomiProject.name,
    title: workImages.nomiProject.title,
    description: workImages.nomiProject.description,
    tags: workImages.nomiProject.tags,
    images: [workImages.nomiProject],
  },
  {
    id: workImages.autoZoneProject.id,
    name: workImages.autoZoneProject.name,
    title: workImages.autoZoneProject.title,
    description: workImages.autoZoneProject.description,
    tags: workImages.autoZoneProject.tags,
    images: [workImages.autoZoneProject],
  },
  {
    id: workImages.greisyResidence.id,
    name: workImages.greisyResidence.name,
    title: workImages.greisyResidence.title,
    description: workImages.greisyResidence.description,
    tags: workImages.greisyResidence.tags,
    images: [workImages.greisyResidence],
  },
  {
    id: workImages.northernTrustBonitaSprings.id,
    name: workImages.northernTrustBonitaSprings.name,
    title: workImages.northernTrustBonitaSprings.title,
    description: workImages.northernTrustBonitaSprings.description,
    tags: workImages.northernTrustBonitaSprings.tags,
    images: [workImages.northernTrustBonitaSprings],
  },
  {
    id: "project-cobs-riverside-park",
    name: "COBS Riverside Park",
    title: "COBS Riverside Park",
    description: "Aluminum Decorative Fence",
    tags: [
      "aluminum fence",
      "decorative fence",
      "park metalwork",
      "monument fence",
      "exterior metalwork",
    ],
    images: [
      workImages.cobsRiversideParkFenceFront,
      workImages.cobsRiversideParkFenceMemorial,
      workImages.cobsRiversideParkFenceRear,
    ],
  },
] as const;
